/**
 * Faqja e detajit të pajisjes S475.
 * Konfigurim: zgjidhni kanalet që doni të shfaqen në Dashboard + buton Ruaj.
 * Outputet (DO0-DO3) vetëm në web; në mobile janë në Dashboard.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

import { BACKEND_URL } from '../config';

const CHANNELS_STORAGE_PREFIX = 's475-dashboard-channels-';
const DEFAULT_CHANNELS = ['DO0', 'DO1', 'DO2', 'DO3'];
const REMOVED_S475_CHANNELS = new Set(['AIN0', 'AIN1', 'AIN2', 'AIN3', 'AIN4', 'AIN5', 'DIN5', 'DIN6', 'DIN7']);
const filterDashboardChannelList = (arr) => (Array.isArray(arr) ? arr.filter((k) => !REMOVED_S475_CHANNELS.has(k)) : []);

const S475Detail = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { hasRole } = useAuth();
  const canEditChannelLabels = hasRole('SUPERADMIN');
  const [device, setDevice] = useState(null);
  const [allChannels, setAllChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [latestValues, setLatestValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [writingDO, setWritingDO] = useState(false);
  const [saving, setSaving] = useState(false);
  const [channelLabels, setChannelLabels] = useState({});
  const [labelEditor, setLabelEditor] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getToken = () => localStorage.getItem('access_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const storageKey = () => `${CHANNELS_STORAGE_PREFIX}${deviceId}`;

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/s475/devices`, { headers: headers() });
        const list = Array.isArray(res.data) ? res.data : [];
        const d = list.find((x) => String(x.id) === String(deviceId));
        setDevice(d || null);
      } catch (e) {
        setDevice(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDevice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (!device) return;
    const raw = device.dashboard_channel_labels;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      setChannelLabels({ ...raw });
    } else {
      setChannelLabels({});
    }
  }, [device]);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/s475/channels`, { headers: headers() });
        const groups = res.data || {};
        const flat = [];
        Object.values(groups).forEach((arr) => {
          if (Array.isArray(arr)) arr.forEach(({ key, label }) => flat.push({ key, label: label || key }));
        });
        setAllChannels(flat);
      } catch (e) {
        setAllChannels([]);
      }
    };
    fetchChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC = back to devices list
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    (async () => {
      let fromApi = null;
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/s475/devices/${deviceId}/dashboard-channels`,
          { headers: headers() }
        );
        const ch = res.data?.channels;
        if (Array.isArray(ch) && ch.length) {
          const filtered = filterDashboardChannelList(ch);
          if (filtered.length) fromApi = filtered;
        }
      } catch (e) {
        /* 404 / rrjet – përdorim localStorage */
      }
      if (cancelled) return;
      if (fromApi) {
        setSelectedChannels(fromApi);
        return;
      }
      try {
        const key = `${CHANNELS_STORAGE_PREFIX}${deviceId}`;
        const raw = localStorage.getItem(key);
        const saved = raw ? JSON.parse(raw) : null;
        const cleaned = filterDashboardChannelList(saved);
        setSelectedChannels(cleaned.length ? cleaned : DEFAULT_CHANNELS);
      } catch (e) {
        setSelectedChannels(DEFAULT_CHANNELS);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const fetchLatest = useCallback(async () => {
    if (!deviceId) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/s475/devices/${deviceId}/latest`, { headers: headers() });
      setLatestValues(res.data);
    } catch (e) {
      setLatestValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    fetchLatest();
    const interval = setInterval(fetchLatest, 5000);
    return () => clearInterval(interval);
  }, [fetchLatest]);

  const handleSaveConfig = async () => {
    setSaving(true);
    let channelsToSave = filterDashboardChannelList(selectedChannels);
    if (!channelsToSave.length) channelsToSave = [...DEFAULT_CHANNELS];
    try {
      await axios.post(
        `${BACKEND_URL}/api/s475/save-dashboard-channels`,
        { device_id: deviceId, channels: channelsToSave },
        { headers: headers() }
      );
      try {
        localStorage.setItem(storageKey(), JSON.stringify(channelsToSave));
      } catch (e) {
        /* cache opsional */
      }
      setSelectedChannels(channelsToSave);
      toast.success('Konfigurimi u ruajt. Dashboard do të shfaqë vetëm kanalet e zgjedhura.');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('errorSaving');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (key) => {
    setSelectedChannels((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const displayChannelTitle = (key, apiLabel) => {
    const c = channelLabels[key];
    if (typeof c === 'string' && c.trim()) return c.trim();
    return apiLabel || key;
  };

  const openLabelEditor = (key, apiLabel) => {
    const base = apiLabel || key;
    setLabelEditor({ key, base });
    const cur = channelLabels[key];
    setLabelDraft(typeof cur === 'string' && cur.trim() ? cur.trim() : base);
  };

  const handleSaveLabelEdit = async () => {
    if (!labelEditor || !deviceId) return;
    const { key, base } = labelEditor;
    const trimmed = labelDraft.trim();
    const next = { ...channelLabels };
    if (!trimmed || trimmed === base) delete next[key];
    else next[key] = trimmed;
    setSavingLabel(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/s475/devices/${deviceId}`,
        { dashboard_channel_labels: next },
        { headers: headers() }
      );
      const updated = res.data?.device;
      if (updated) setDevice(updated);
      setChannelLabels(next);
      toast.success('Emri u ruajt; do të shfaqet njëjtë në Dashboard.');
      setLabelEditor(null);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('errorSaving');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSavingLabel(false);
    }
  };

  const getCurrentDOValues = () => {
    const doV = latestValues?.values?.do_values || {};
    return {
      DO0: !!(doV.DO0 === 1 || doV.DO0 === true),
      DO1: !!(doV.DO1 === 1 || doV.DO1 === true),
      DO2: !!(doV.DO2 === 1 || doV.DO2 === true),
      DO3: !!(doV.DO3 === 1 || doV.DO3 === true),
    };
  };

  const handleToggleDO = async (key) => {
    const current = getCurrentDOValues();
    const newVal = !current[key];
    const values = { ...current, [key]: newVal };
    setWritingDO(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/s475/devices/${deviceId}/write-do`,
        { values, changed: key },
        { headers: headers() }
      );
      toast.success(`${key} u vendos në ${newVal ? 'ON' : 'OFF'}`);
      fetchLatest();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('writeError');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setWritingDO(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full"><p className="text-gray-500">{t('loading')}</p></div>
      </Layout>
    );
  }

  if (!device) {
    return (
      <Layout>
        <div className="p-4">
          <button onClick={() => navigate('/devices')} className="text-cyan-500 hover:underline mb-4">← {t('backToDevices')}</button>
          <p className="text-gray-500">{t('deviceNotFound')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full flex flex-col bg-gray-100 dark:bg-[#0f172a] p-4 overflow-auto">
        <div className="mb-6 flex-shrink-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{device.label}</h1>
        </div>

        {/* Konfigurim: Zgjidhni çfarë të shfaqet në Dashboard */}
        {isMobile ? (
          /* Mobile: stil "Navigimi i Lejuar" – listë kompakte me checkbox, Anulo + Ruaj */
          <div className="mb-4 rounded-2xl w-full max-w-sm overflow-hidden bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">{t('configForDashboard')}</h2>
            </div>
            <p className="px-4 pt-2 text-xs text-gray-600 dark:text-gray-400">Zgjidhni kanalet që doni të shfaqen në Dashboard.</p>
            <div className="p-2 max-h-64 overflow-y-auto">
              {allChannels.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  <label className="flex flex-1 items-center px-2 py-2 cursor-pointer rounded-lg transition min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedChannels.includes(key)}
                      onChange={() => toggleChannel(key)}
                      className="w-4 h-4 mr-3 shrink-0 rounded border-gray-300 dark:border-gray-500 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayChannelTitle(key, label)}</span>
                  </label>
                  {canEditChannelLabels && (
                    <button
                      type="button"
                      onClick={() => openLabelEditor(key, label)}
                      className="shrink-0 p-2 rounded-lg text-cyan-600 dark:text-cyan-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                      title="Ndrysho emrin"
                      aria-label="Ndrysho emrin"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button type="button" onClick={() => navigate('/devices')} style={{ height: '32px' }} className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition">{t('cancel')}</button>
              <button type="button" onClick={handleSaveConfig} disabled={saving} style={{ height: '32px' }} className="flex-1 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving ? '...' : 'Ruaj'}</button>
            </div>
          </div>
        ) : (
          /* Web: kartat si outputet – klik ndryshon ngjyrën kur e zgjedhësh */
          <div className="mb-8 p-4 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155]">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Konfigurim për Dashboard</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Zgjidhni kanalet që doni të shfaqen në Dashboard. Klikoni për të zgjedhur; zgjedhja ndryshon ngjyrën.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-4">
              {allChannels.map(({ key, label }) => {
                const isSelected = selectedChannels.includes(key);
                const title = displayChannelTitle(key, label);
                return (
                  <div key={key} className="relative group">
                    <button
                      type="button"
                      onClick={() => toggleChannel(key)}
                      className={`w-full rounded-xl p-4 border min-h-[72px] flex flex-col justify-center text-left transition-colors pr-9 ${
                        isSelected
                          ? 'bg-green-600 dark:bg-green-600 border-green-500 text-white hover:bg-green-500'
                          : 'bg-white dark:bg-[#1e293b] border-gray-200 dark:border-[#334155] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2d3a4f]'
                      }`}
                    >
                      <span className="text-sm font-medium truncate">{title}</span>
                      <span className="text-xs opacity-80 mt-0.5">{isSelected ? t('selected') : t('clickToSelect')}</span>
                    </button>
                    {canEditChannelLabels && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openLabelEditor(key, label);
                        }}
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 text-current"
                        title="Ndrysho emrin në Dashboard"
                        aria-label="Ndrysho emrin"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <button type="button" onClick={handleSaveConfig} disabled={saving} className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold disabled:opacity-60">{saving ? t('saving') : t('save')}</button>
          </div>
        )}

        {latestValues?.ts_iso && (
          <div className="mt-4 text-xs text-gray-500">{t('lastLabel')}: {latestValues.ts_iso}</div>
        )}

        {labelEditor && (
          <div
            role="presentation"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            onClick={() => !savingLabel && setLabelEditor(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="bg-white dark:bg-gray-800 rounded-xl p-4 max-w-md w-full shadow-xl border border-gray-200 dark:border-gray-600"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Emri në Dashboard</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Kanali: {labelEditor.key}</p>
              <input
                type="text"
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                maxLength={120}
                className="mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-white"
              />
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  type="button"
                  disabled={savingLabel}
                  onClick={() => setLabelEditor(null)}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white"
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  disabled={savingLabel}
                  onClick={handleSaveLabelEdit}
                  className="px-4 py-2 rounded-lg bg-cyan-600 text-white font-medium disabled:opacity-60"
                >
                  {savingLabel ? '…' : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default S475Detail;
